/**
 * CR130 Phase 3: Deal Grid With Details
 * 
 * A grid of deal cards with details mode support.
 * Handles state management for expand/panel modes at the list level.
 * 
 * Use this instead of manually mapping over deals + DealGiftCard when
 * you need details mode functionality.
 * 
 * CR130 Review Fix: Updated layout to support fixed-width panel alongside cards.
 * When panel is open, grid shrinks to accommodate the 400px panel on the right.
 */

import { useMemo } from "react";
import { DealCardWithDetails } from "./DealCardWithDetails";
import { DealDetailsPanel } from "./DealDetailsPanel";
import { DealDetailsProvider, useDealDetails } from "@/contexts/DealDetailsContext";
import { useDealCardUX } from "@/contexts/DealCardUXContext";
import type { Deal } from "@/lib/types/deals";
import { cn } from "@/lib/utils";

interface DealGridWithDetailsProps {
  deals: (Deal & { id?: string; dedupe_key?: string })[];
  isAdmin?: boolean;
  /** Additional class for the grid container */
  className?: string;
  /** Panel width in pixels (default 400) */
  panelWidth?: number;
}

// Inner component that uses the details context
function DealGridInner({
  deals,
  isAdmin,
  className,
  panelWidth = 400,
}: DealGridWithDetailsProps) {
  const { detailsMode, detailsSections } = useDealCardUX();
  const { selectedDeal, closePanel } = useDealDetails();
  
  // Generate stable IDs for deals
  const dealsWithIds = useMemo(() => {
    return deals.map((deal, idx) => ({
      deal,
      dealId: deal.id || deal.dedupe_key || `${deal.merchant}-${deal.gift_card_brand}-${idx}`,
    }));
  }, [deals]);
  
  const isPanelOpen = detailsMode === "panel" && !!selectedDeal;
  
  return (
    <>
      {/* Main content area - shrinks when panel is open */}
      <div 
        className="transition-all duration-200"
        style={{ 
          marginRight: isPanelOpen ? `${panelWidth}px` : 0,
        }}
      >
        <div className={className}>
          {dealsWithIds.map(({ deal, dealId }) => (
            <DealCardWithDetails
              key={dealId}
              deal={deal}
              dealId={dealId}
              isAdmin={isAdmin}
            />
          ))}
        </div>
      </div>
      
      {/* Panel mode: render the fixed side panel */}
      {detailsMode === "panel" && (
        <DealDetailsPanel
          deal={selectedDeal}
          sectionsConfig={detailsSections}
          isAdmin={isAdmin}
          isOpen={!!selectedDeal}
          onClose={closePanel}
          width={panelWidth}
        />
      )}
    </>
  );
}

export function DealGridWithDetails({
  deals,
  isAdmin = false,
  className = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4",
  panelWidth = 400,
}: DealGridWithDetailsProps) {
  const { detailsMode, expandSingleOpen, detailsSections } = useDealCardUX();
  
  return (
    <DealDetailsProvider
      mode={detailsMode}
      singleExpandMode={expandSingleOpen}
      sectionsConfig={detailsSections}
    >
      <DealGridInner
        deals={deals}
        isAdmin={isAdmin}
        className={className}
        panelWidth={panelWidth}
      />
    </DealDetailsProvider>
  );
}

export default DealGridWithDetails;
